/**
 * Registry Device API Service
 * Provides methods to interact with registry device endpoints
 * Handles all device registry operations for BodyTrace, SmartMeter, and other devices
 */

import ormaApi from './OrmaApi';

/**
 * Fetch BodyTrace/Registry devices by query parameters
 * @param {Object} query - MongoDB query object
 * @param {Object} options - Query options (sort, limit, skip, projection)
 * @returns {Promise<Array>} Array of device registry entries
 */
export const fetchBodytraceDevices = async (query = {}, options = {}) => {
  try {
    const response = await ormaApi.post('/api/registry/devices', {
      query,
      ...options
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching BodyTrace devices:', error);
    throw error;
  }
};

/**
 * Get devices by member ID
 * @param {string} memberId - The member ID
 * @returns {Promise<Array>} Array of device registry entries for the member
 */
export const getDevicesByMemberId = async (memberId) => {
  try {
    const response = await ormaApi.get(`/api/registry/member/${memberId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching devices by member ID:', error);
    throw error;
  }
};

/**
 * Delete a device registry entry
 * @param {string} deviceId - The device ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteDevice = async (deviceId) => {
  try {
    const response = await ormaApi.delete(`/api/registry/device/${deviceId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting device:', error);
    throw error;
  }
};

/**
 * Change device ownership
 * @param {string} imei - The device IMEI
 * @param {Object} newOwnerInfo - New owner information
 * @returns {Promise<Object>} Updated device with ownership change
 */
export const changeDeviceOwnership = async (imei, newOwnerInfo) => {
  try {
    const response = await ormaApi.post('/api/registry/updateOwnership', {
      imei,
      newOwnerInfo
    });
    return response.data;
  } catch (error) {
    console.error('Error changing device ownership:', error);
    throw error;
  }
};

/**
 * Get comprehensive device counts
 * @param {string} groupId - Optional group ID to filter counts
 * @returns {Promise<Object>} Comprehensive device counts
 */
export const getDeviceCounts = async (groupId = null) => {
  try {
    const params = groupId ? { groupId } : {};
    const response = await ormaApi.get('/api/registry/counts', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching device counts:', error);
    throw error;
  }
};

/**
 * Order a BodyTrace device for a member
 * @param {string} memberId - The member ID
 * @param {string} device - The device type (e.g., 'BT004', 'BT105')
 * @returns {Promise<Object>} Order response
 */
export const orderBodyTraceDevice = async (memberId, device) => {
  try {
    const response = await ormaApi.post('/api/registry/order-device', {
      memberId,
      device,
    });
    return response.data;
  } catch (error) {
    console.error('Error ordering BodyTrace device:', error);
    throw error;
  }
};

/**
 * Retrieve BodyTrace device data for a member
 * @param {string} memberId - The member ID
 * @param {string} imei - The device IMEI
 * @returns {Promise<Object>} Retrieved data response
 */
export const retrieveBodyTraceData = async (memberId, imei) => {
  try {
    const response = await ormaApi.post('/api/registry/retrieve-data', {
      memberId,
      imei,
    });
    return response.data;
  } catch (error) {
    console.error('Error retrieving BodyTrace data:', error);
    throw error;
  }
};

/**
 * Connect an existing device to a member
 * @param {string} memberId - The member ID
 * @param {string} device - The device type (e.g., 'BT004', 'BT105', 'SMIG')
 * @param {string} imei - The device IMEI
 * @returns {Promise<Object>} Connection response
 */
export const connectExistingDevice = async (memberId, device, imei) => {
  try {
    const response = await ormaApi.post('/api/registry/connect-device', {
      memberId,
      device,
      imei,
    });
    return response.data;
  } catch (error) {
    console.error('Error connecting existing device:', error);
    throw error;
  }
};

/**
 * Disconnect a device from a member
 * @param {string} memberId - The member ID
 * @param {string} imei - The device IMEI
 * @param {string} reason - Reason for disconnection
 * @returns {Promise<Object>} Disconnect response
 */
export const disconnectDevice = async (memberId, imei, reason) => {
  try {
    const response = await ormaApi.post('/api/registry/disconnect-device', {
      memberId,
      imei,
      reason,
    });
    return response.data;
  } catch (error) {
    console.error('Error disconnecting device:', error);
    throw error;
  }
};

/**
 * Update device calibration settings
 * @param {string} deviceId - The device registry ID
 * @param {Object} calibrationData - Calibration offsets
 * @param {number} calibrationData.calibrationDiffWeight - Weight offset (for BT004)
 * @param {number} calibrationData.calibrationDiffSystolic - Systolic BP offset (for BT105)
 * @param {number} calibrationData.calibrationDiffDiastolic - Diastolic BP offset (for BT105)
 * @returns {Promise<Object>} Update response
 */
export const updateDeviceCalibration = async (deviceId, calibrationData) => {
  try {
    const response = await ormaApi.put(
      `/api/registry/device/${deviceId}/calibration`,
      calibrationData
    );
    return response.data;
  } catch (error) {
    console.error('Error updating device calibration:', error);
    throw error;
  }
};